/**
 * parseApiDateTime — read a DRF-rendered activity timestamp as UK LOCAL wall-clock
 * time, ignoring the misleading literal "Z" the backend appends.
 *
 * Root cause (Timeline "in an hour" bug): DRF is configured with
 * `TIME_ZONE = "Europe/London"` and `DATETIME_FORMAT = "%d-%m-%Y %H:%M:%S.%fZ"`.
 * That "Z" is a hardcoded literal character in the strftime format, NOT an
 * indicator that the value is UTC — every timestamp is actually Europe/London
 * wall-clock time (BST in summer, GMT in winter). Parsing it as real UTC (as
 * moment does when it sees a trailing 'Z' format token) makes every activity
 * look ~1 hour in the future during BST.
 *
 * This helper re-interprets the BE's `DD-MM-YYYY HH:mm:ss[.ffffff]Z` shape as
 * Europe/London wall-clock time using dayjs's IANA timezone database (via the
 * `utc` + `timezone` + `customParseFormat` plugins bundled with the `dayjs`
 * dependency already in package.json — no new dependency added), so the
 * BST↔GMT offset is resolved automatically and correctly on both sides of a
 * clock-change (e.g. the 25 Oct 2026 transition), regardless of the viewer's
 * own browser timezone.
 *
 * It ALSO accepts genuine ISO-8601 strings that carry a real explicit offset
 * (e.g. `2026-09-24T15:41:33+00:00`) — those are honoured as given, not
 * reinterpreted as UK time — day-first strings that carry a REAL explicit
 * `±HH:MM`/`±HHMM` offset (e.g. `28-06-2026 17:21:36.196000+01:00`, as opposed
 * to the misleading literal 'Z') — also honoured as given — and bare
 * `DD-MM-YYYY` date-only strings.
 *
 * Returns a `moment` instance (so existing call sites can keep using the full
 * moment API — `.fromNow()`, `.isSameOrAfter()`, `.format()`, `.diff()`,
 * `.clone()`, `.startOf()`, `.year()` — unchanged) representing the correct
 * absolute instant, or `null` for empty/invalid input.
 */
import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const UK_TIMEZONE = 'Europe/London';

// `DD-MM-YYYY` with NO time component — a bare date, no timezone ambiguity.
const DATE_ONLY_RE = /^(\d{2})-(\d{2})-(\d{4})$/;

// The BE's shape: `DD-MM-YYYY HH:mm:ss[.ffffff...]` with an optional trailing
// literal 'Z' (always misleading — never means real UTC here). Also matches
// the no-fraction / no-'Z' variants the same field can be rendered without.
const UK_WALL_CLOCK_RE = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?Z?$/;

// Day-first with a REAL explicit numeric offset, e.g.
// `28-06-2026 17:21:36.196000+01:00` (or `+0100`). Unlike a trailing literal
// 'Z', a genuine `±HH:MM`/`±HHMM` offset is an explicit instant, not the BE's
// misleading UK-local marker — honour it as-is instead of reinterpreting as
// UK local time. Reorder to ISO and let dayjs parse the offset natively.
const UK_DATE_WITH_OFFSET_RE = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?([+-]\d{2}:?\d{2})$/;

// Truncate sub-millisecond precision (BE sends microseconds) before handing a
// string to the native/ISO parser, which only understands milliseconds.
const truncateToMillis = (s) => s.replace(/(\.\d{3})\d+/, '$1');

/**
 * @param {string|null|undefined} value
 * @returns {import('moment').Moment|null}
 */
export function parseApiDateTime(value) {
    if (value === null || value === undefined) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    if (DATE_ONLY_RE.test(raw)) {
        const m = moment(raw, 'DD-MM-YYYY', true);
        return m.isValid() ? m : null;
    }

    const offsetMatch = raw.match(UK_DATE_WITH_OFFSET_RE);
    if (offsetMatch) {
        const [, dd, mm, yyyy, hh, min, ss, frac, offset] = offsetMatch;
        const ms = frac ? Math.round(Number(`0.${frac}`) * 1000) : 0;
        const iso = `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}.${String(ms).padStart(3, '0')}${offset}`;
        // Validate the calendar fields strictly (see comment below) before
        // trusting dayjs's lenient rollover parse.
        if (!moment(`${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}.${String(ms).padStart(3, '0')}`, 'YYYY-MM-DD HH:mm:ss.SSS', true).isValid()) return null;
        const d = dayjs(iso);
        return d.isValid() ? moment(d.valueOf()) : null;
    }

    const ukMatch = raw.match(UK_WALL_CLOCK_RE);
    if (ukMatch) {
        const [, dd, mm, yyyy, hh, min, ss, frac] = ukMatch;
        const ms = frac ? Math.round(Number(`0.${frac}`) * 1000) : 0;
        const wallClock = `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}.${String(ms).padStart(3, '0')}`;
        // dayjs's customParseFormat rolls invalid calendar values over (e.g. month 13,
        // hour 99) instead of rejecting them — validate the calendar fields strictly
        // with moment first (no timezone involved here, just calendar arithmetic).
        if (!moment(wallClock, 'YYYY-MM-DD HH:mm:ss.SSS', true).isValid()) return null;
        // NOTE — 25 Oct 2026 fall-back ambiguity: the clocks-back hour (01:00-01:59
        // UK local) occurs twice — once as BST (UTC+1), once as GMT (UTC+0) an hour
        // later — and the BE's format carries no offset to disambiguate which
        // occurrence a value in that hour means. dayjs.tz (like most tz libraries)
        // resolves an ambiguous local time to its FIRST occurrence, i.e. BST/UTC+1.
        // This is an unavoidable one-hour ambiguity inherent to the BE emitting
        // wall-clock time with a fake 'Z' instead of a real offset/UTC instant —
        // not something a parser can fix without more information from the BE.
        const d = dayjs.tz(wallClock, 'YYYY-MM-DD HH:mm:ss.SSS', UK_TIMEZONE);
        if (!d.isValid()) return null;
        return moment(d.valueOf());
    }

    // Fallback: genuine ISO-8601 (with an explicit offset, 'Z', or none —
    // dayjs/native parsing honours whatever offset is actually present).
    const iso = dayjs(truncateToMillis(raw));
    return iso.isValid() ? moment(iso.valueOf()) : null;
}

export default parseApiDateTime;
